import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface MisVentasProps {
  userId: string;
  profileId: string;
}

type MetodoEnvio = 'usps_ground' | 'usps_priority';

const SERVICIOS_USPS = [
  {
    id: 'usps_ground' as MetodoEnvio,
    nombre: 'USPS Ground Advantage',
    descripcion: '3-5 días hábiles · Más económico',
    icono: 'ri-mail-send-line',
    colorActivo: 'border-gray-900 bg-gray-50',
    colorInactivo: 'border-gray-200 bg-white',
  },
  {
    id: 'usps_priority' as MetodoEnvio,
    nombre: 'USPS Priority Mail',
    descripcion: '1-3 días hábiles · Más rápido',
    icono: 'ri-rocket-line',
    colorActivo: 'border-red-500 bg-red-50',
    colorInactivo: 'border-gray-200 bg-white',
  },
];

function calcularCostoUsps(servicio: MetodoEnvio, pesoLbs: number): number {
  const peso = Math.max(0.5, pesoLbs || 1.0);
  if (servicio === 'usps_ground') {
    if (peso <= 1) return 4.50;
    if (peso <= 2) return 5.00;
    if (peso <= 5) return 6.50;
    if (peso <= 10) return 9.00;
    if (peso <= 15) return 12.00;
    if (peso <= 20) return 15.00;
    return 15.00 + (peso - 20) * 0.60;
  }
  if (peso <= 1) return 8.50;
  if (peso <= 2) return 9.50;
  if (peso <= 5) return 12.00;
  if (peso <= 10) return 16.00;
  if (peso <= 15) return 20.00;
  if (peso <= 20) return 24.00;
  return 24.00 + (peso - 20) * 0.90;
}

// ── Estado de envío ────────────────────────────────────────────────────────
type EstadoEnvio = 'pendiente' | 'enviado' | 'entregado' | 'no_aplica';

function getEstadoEnvio(venta: any): EstadoEnvio {
  if (venta.tipo_orden === 'reserva_auto') return 'no_aplica';
  if (venta.metodo_envio === 'islandwide' || venta.tipo_envio === 'islandwide') return 'no_aplica';
  if (venta.estado === 'entregado' || venta.fondos_liberados) return 'entregado';
  if (venta.estado === 'enviado' || venta.etiqueta_generada) return 'enviado';
  if (venta.estado === 'pagado') return 'pendiente';
  return 'no_aplica';
}

const ENVIO_CONFIG: Record<EstadoEnvio, { label: string; cls: string; icono: string; dot: string }> = {
  pendiente: {
    label: 'Pendiente de Envío',
    cls: 'bg-amber-50 text-amber-700 border border-amber-200',
    icono: 'ri-time-line',
    dot: 'bg-amber-400',
  },
  enviado: {
    label: 'Enviado',
    cls: 'bg-green-50 text-green-700 border border-green-200',
    icono: 'ri-mail-send-line',
    dot: 'bg-green-500',
  },
  entregado: {
    label: 'Entregado',
    cls: 'bg-gray-100 text-gray-600 border border-gray-200',
    icono: 'ri-checkbox-circle-line',
    dot: 'bg-gray-400',
  },
  no_aplica: {
    label: '—',
    cls: 'bg-gray-50 text-gray-400 border border-gray-100',
    icono: 'ri-subtract-line',
    dot: 'bg-gray-300',
  },
};

const MisVentas = ({ userId, profileId }: MisVentasProps) => {
  const [ventas, setVentas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState<'todas' | 'pendientes' | 'enviadas'>('todas');

  // Modal etiqueta USPS
  const [etiquetaModal, setEtiquetaModal] = useState<any>(null);
  const [servicioSeleccionado, setServicioSeleccionado] = useState<MetodoEnvio>('usps_ground');
  const [generando, setGenerando] = useState(false);
  const [resultadoEtiqueta, setResultadoEtiqueta] = useState<any>(null);

  // Modal tracking / liberar fondos
  const [trackingModal, setTrackingModal] = useState<any>(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [procesandoTracking, setProcesandoTracking] = useState(false);
  const [trackingSuccess, setTrackingSuccess] = useState(false);

  useEffect(() => {
    fetchVentas();
  }, [profileId]);

  const fetchVentas = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('orders')
      .select('*, products(titulo, peso_libras, largo_in, ancho_in, alto_in, zip_origen, es_carga_pesada)')
      .eq('vendedor_id', profileId)
      .order('created_at', { ascending: false });

    if (!error && data) setVentas(data);
    setLoading(false);
  };

  const handleGenerarEtiquetaUsps = async () => {
    if (!etiquetaModal) return;
    setGenerando(true);
    setResultadoEtiqueta(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-usps-label`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            orderId: etiquetaModal.id,
            servicioUsps: servicioSeleccionado,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setResultadoEtiqueta(data);
        fetchVentas();
      } else {
        throw new Error(data.error || 'Error generando etiqueta');
      }
    } catch (error: any) {
      console.error('Error generando etiqueta USPS:', error);
      setResultadoEtiqueta({ error: error.message || 'Error al generar la etiqueta. Intenta de nuevo.' });
    } finally {
      setGenerando(false);
    }
  };

  const notificarCambioEstado = async (orderId: string, nuevoEstado: string, token: string) => {
    try {
      await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/order-status-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ orderId, nuevoEstado }),
        }
      );
    } catch (err) {
      console.warn('No se pudo enviar email de estado:', err);
    }
  };

  const handleLiberarFondos = async () => {
    if (!trackingModal || !trackingNumber.trim()) return;
    setProcesandoTracking(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token ?? '';

      await notificarCambioEstado(trackingModal.id, 'enviado', token);

      const emailResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-tracking-email`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ orderId: trackingModal.id, trackingNumber: trackingNumber.trim() }),
        }
      );
      const emailData = await emailResponse.json();
      if (!emailData.success) console.warn('Email tracking no enviado:', emailData.error);

      const fundsResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/release-seller-funds`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ orderId: trackingModal.id, trackingNumber: trackingNumber.trim() }),
        }
      );

      const fundsData = await fundsResponse.json();
      if (fundsData.success) {
        await notificarCambioEstado(trackingModal.id, 'entregado', token);
        setTrackingSuccess(true);
        fetchVentas();
      } else {
        throw new Error(fundsData.error || 'Error al liberar fondos');
      }
    } catch (error) {
      console.error('Error procesando tracking:', error);
      alert('Hubo un error al procesar. Intenta de nuevo.');
    } finally {
      setProcesandoTracking(false);
    }
  };

  const getEstadoBadge = (estado: string) => {
    const map: Record<string, { label: string; cls: string }> = {
      pagado: { label: 'Pagado', cls: 'bg-green-100 text-green-700' },
      pendiente: { label: 'Pendiente', cls: 'bg-yellow-100 text-yellow-700' },
      enviado: { label: 'Enviado', cls: 'bg-green-100 text-green-700' },
      entregado: { label: 'Entregado', cls: 'bg-gray-100 text-gray-600' },
      completado: { label: 'Completado', cls: 'bg-green-100 text-green-700' },
      cancelado: { label: 'Cancelado', cls: 'bg-red-100 text-red-700' },
    };
    return map[estado] || { label: estado, cls: 'bg-gray-100 text-gray-600' };
  };

  const ventasFiltradas = ventas.filter((v) => {
    if (filtro === 'pendientes') return getEstadoEnvio(v) === 'pendiente';
    if (filtro === 'enviadas') return getEstadoEnvio(v) === 'enviado' || getEstadoEnvio(v) === 'entregado';
    return true;
  });

  const totalGanado = ventas
    .filter(v => v.estado === 'pagado' || v.estado === 'completado' || v.estado === 'enviado' || v.estado === 'entregado')
    .reduce((sum, v) => sum + (v.monto_vendedor || 0), 0);

  const pendientesEnvio = ventas.filter(v => getEstadoEnvio(v) === 'pendiente').length;
  const enviadas = ventas.filter(v => getEstadoEnvio(v) === 'enviado' || getEstadoEnvio(v) === 'entregado').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      {/* ── Resumen ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Total Ventas</p>
          <p className="text-2xl font-bold text-gray-900">{ventas.length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Ganado (neto)</p>
          <p className="text-2xl font-bold text-green-700">${totalGanado.toFixed(2)}</p>
        </div>
        <div
          className="bg-amber-50 border border-amber-200 rounded-xl p-4 cursor-pointer hover:border-amber-400 transition-colors"
          onClick={() => setFiltro('pendientes')}
        >
          <p className="text-xs text-amber-600 mb-1">Pendientes de Envío</p>
          <p className="text-2xl font-bold text-amber-700">{pendientesEnvio}</p>
        </div>
        <div
          className="bg-green-50 border border-green-200 rounded-xl p-4 cursor-pointer hover:border-green-400 transition-colors"
          onClick={() => setFiltro('enviadas')}
        >
          <p className="text-xs text-green-600 mb-1">Enviadas</p>
          <p className="text-2xl font-bold text-green-700">{enviadas}</p>
        </div>
      </div>

      {/* ── Filtros ───────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-4">
        {(['todas', 'pendientes', 'enviadas'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFiltro(f)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
              filtro === f
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f === 'todas' ? 'Todas' : f === 'pendientes' ? 'Pendientes de Envío' : 'Enviadas'}
          </button>
        ))}
      </div>

      {/* ── Banner USPS ───────────────────────────────────────────────────── */}
      {pendientesEnvio > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-time-line text-xl text-amber-600"></i>
          </div>
          <div className="flex-1">
            <p className="font-bold text-amber-900 text-sm">
              {pendientesEnvio} {pendientesEnvio === 1 ? 'orden pendiente' : 'órdenes pendientes'} de envío
            </p>
            <p className="text-amber-700 text-xs mt-0.5">
              Genera la etiqueta USPS y deposita el paquete en cualquier correo postal de PR.
            </p>
          </div>
        </div>
      )}

      {/* ── Lista de ventas ───────────────────────────────────────────────── */}
      {ventasFiltradas.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-shopping-bag-line text-3xl text-gray-400"></i>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">
            {filtro === 'todas' ? 'Aún no tienes ventas' : 'No hay órdenes en esta categoría'}
          </h3>
          <p className="text-gray-500 text-sm">
            {filtro === 'todas'
              ? 'Cuando alguien compre tus artículos, aparecerán aquí.'
              : 'Prueba cambiando el filtro.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {ventasFiltradas.map((venta) => {
            const estadoBadge = getEstadoBadge(venta.estado);
            const estadoEnvio = getEstadoEnvio(venta);
            const envioConfig = ENVIO_CONFIG[estadoEnvio];
            const puedeGenerarEtiqueta = estadoEnvio === 'pendiente';
            const puedeLiberarFondos = venta.estado === 'enviado' && !venta.fondos_liberados && venta.etiqueta_generada;
            const pesoLbs = venta.products?.peso_libras || 1.0;

            return (
              <div
                key={venta.id}
                className={`bg-white border rounded-xl overflow-hidden transition-colors ${
                  puedeGenerarEtiqueta ? 'border-amber-200 hover:border-amber-300' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Barra de estado de envío */}
                {estadoEnvio !== 'no_aplica' && (
                  <div className={`px-4 py-2 flex items-center gap-2 text-xs font-semibold ${
                    estadoEnvio === 'pendiente'
                      ? 'bg-amber-50 border-b border-amber-100'
                      : estadoEnvio === 'enviado'
                      ? 'bg-green-50 border-b border-green-100'
                      : 'bg-gray-50 border-b border-gray-100'
                  }`}>
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${envioConfig.dot}`}></span>
                    <i className={`${envioConfig.icono} text-sm`}></i>
                    <span>Estado del Envío: <strong>{envioConfig.label}</strong></span>
                    {venta.usps_tracking_number && (
                      <a
                        href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${venta.usps_tracking_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto font-mono text-green-700 hover:underline flex items-center gap-1"
                      >
                        {venta.usps_tracking_number.slice(0, 20)}...
                        <i className="ri-external-link-line"></i>
                      </a>
                    )}
                  </div>
                )}

                <div className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    {/* Info de la orden */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${estadoBadge.cls}`}>
                          {estadoBadge.label}
                        </span>
                        {venta.etiqueta_generada && (
                          <span className="flex items-center gap-1 px-2.5 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">
                            <i className="ri-printer-line"></i>
                            Etiqueta Generada
                          </span>
                        )}
                        {venta.fondos_liberados && (
                          <span className="flex items-center gap-1 px-2.5 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                            <i className="ri-check-double-line"></i>
                            Fondos Liberados
                          </span>
                        )}
                        {venta.tipo_orden === 'reserva_auto' && (
                          <span className="px-2.5 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
                            Reserva Auto
                          </span>
                        )}
                      </div>

                      <p className="text-sm font-bold text-gray-900 mb-1">
                        Orden #{venta.id.slice(0, 8).toUpperCase()}
                      </p>

                      {venta.products?.titulo && (
                        <p className="text-xs text-gray-500 mb-2 line-clamp-1">
                          <i className="ri-box-3-line mr-1"></i>
                          {venta.products.titulo}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-money-dollar-circle-line"></i>
                          Total: <strong className="text-gray-700">${venta.monto_total?.toFixed(2)}</strong>
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-wallet-line text-green-500"></i>
                          Tu ganancia: <strong className="text-green-700">${venta.monto_vendedor?.toFixed(2)}</strong>
                        </span>
                        {pesoLbs > 0 && (
                          <span className="flex items-center gap-1">
                            <i className="ri-scales-line"></i>
                            {pesoLbs} lbs
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {new Date(venta.created_at).toLocaleDateString('es-PR')}
                        </span>
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="flex flex-col gap-2 sm:items-end sm:min-w-[180px]">
                      {/* BOTÓN PRINCIPAL: Generar Etiqueta */}
                      {puedeGenerarEtiqueta && (
                        <button
                          onClick={() => {
                            setEtiquetaModal(venta);
                            setResultadoEtiqueta(null);
                            setServicioSeleccionado('usps_ground');
                          }}
                          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-sm"
                        >
                          <i className="ri-printer-line"></i>
                          Generar Etiqueta USPS
                        </button>
                      )}

                      {/* Descargar etiqueta si ya fue generada */}
                      {venta.usps_label_url && (
                        <a
                          href={venta.usps_label_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors whitespace-nowrap text-sm"
                        >
                          <i className="ri-download-line"></i>
                          Descargar PDF
                        </a>
                      )}

                      {/* Ver tracking si existe */}
                      {venta.usps_tracking_number && (
                        <a
                          href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${venta.usps_tracking_number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 w-full px-4 py-2 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap text-xs"
                        >
                          <i className="ri-map-pin-line"></i>
                          Ver Tracking USPS
                        </a>
                      )}

                      {/* Liberar fondos */}
                      {puedeLiberarFondos && (
                        <button
                          onClick={() => setTrackingModal(venta)}
                          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                        >
                          <i className="ri-check-double-line"></i>
                          Marcar Entregado
                        </button>
                      )}

                      {venta.fondos_liberados && (
                        <span className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-gray-100 text-gray-500 font-semibold rounded-xl text-sm">
                          <i className="ri-check-line"></i>
                          Completado
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL: Generar Etiqueta USPS
      ══════════════════════════════════════════════════════════════════════ */}
      {etiquetaModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full my-8">

            {!resultadoEtiqueta ? (
              <>
                {/* Header */}
                <div className="bg-gray-900 rounded-t-2xl p-6 text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                      <i className="ri-printer-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">Generar Etiqueta USPS</h3>
                      <p className="text-gray-400 text-xs">Orden #{etiquetaModal.id.slice(0, 8).toUpperCase()}</p>
                    </div>
                    <button
                      onClick={() => setEtiquetaModal(null)}
                      className="ml-auto w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line text-white"></i>
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-5">
                  {/* Info del paquete */}
                  <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Artículo</span>
                      <span className="font-semibold text-gray-900 text-right max-w-[220px] line-clamp-1">
                        {etiquetaModal.products?.titulo || 'Artículo'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Peso</span>
                      <span className="font-semibold text-gray-900">{etiquetaModal.products?.peso_libras || 1.0} lbs</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Tu ganancia</span>
                      <span className="font-bold text-green-700">${etiquetaModal.monto_vendedor?.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Selección de servicio */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">
                      Elige el servicio de envío
                    </label>
                    <div className="space-y-3">
                      {SERVICIOS_USPS.map((servicio) => {
                        const pesoLbs = etiquetaModal.products?.peso_libras || 1.0;
                        const costo = calcularCostoUsps(servicio.id, pesoLbs);
                        const activo = servicioSeleccionado === servicio.id;
                        return (
                          <button
                            key={servicio.id}
                            type="button"
                            onClick={() => setServicioSeleccionado(servicio.id)}
                            className={`w-full p-4 border-2 rounded-xl transition-all text-left cursor-pointer ${
                              activo ? servicio.colorActivo : servicio.colorInactivo
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${activo ? 'bg-gray-900' : 'bg-gray-100'}`}>
                                  <i className={`${servicio.icono} text-base ${activo ? 'text-white' : 'text-gray-500'}`}></i>
                                </div>
                                <div>
                                  <p className="font-bold text-gray-900 text-sm">{servicio.nombre}</p>
                                  <p className="text-xs text-gray-500">{servicio.descripcion}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-gray-900 text-base">${costo.toFixed(2)}</p>
                                <p className="text-xs text-gray-400">etiqueta</p>
                              </div>
                            </div>
                          </button>
                        );
                      })}

                      {/* Placeholder Islandwide */}
                      <div className="w-full p-4 border-2 border-dashed border-gray-200 rounded-xl opacity-40">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                            <i className="ri-truck-line text-base text-gray-400"></i>
                          </div>
                          <div>
                            <p className="font-bold text-gray-500 text-sm">Island Wide – Recogida a Domicilio</p>
                            <p className="text-xs text-gray-400">Próximamente disponible</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Resumen de costo */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Costo de etiqueta</span>
                      <span className="font-bold text-gray-900">
                        -${calcularCostoUsps(servicioSeleccionado, etiquetaModal.products?.peso_libras || 1.0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-gray-200 pt-2">
                      <span className="text-gray-600">Tu ganancia neta</span>
                      <span className="font-bold text-green-700">
                        ${Math.max(0, (etiquetaModal.monto_vendedor || 0) - calcularCostoUsps(servicioSeleccionado, etiquetaModal.products?.peso_libras || 1.0)).toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 flex items-start gap-1 pt-1">
                      <i className="ri-information-line flex-shrink-0 mt-0.5"></i>
                      El costo de la etiqueta se descuenta de tu ganancia de la venta.
                    </p>
                  </div>

                  {/* Instrucciones */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <h4 className="font-bold text-amber-900 text-sm mb-2 flex items-center gap-2">
                      <i className="ri-map-pin-line"></i>
                      Cómo depositar tu paquete
                    </h4>
                    <ol className="space-y-1.5 text-xs text-amber-800">
                      <li className="flex items-start gap-2"><span className="font-bold flex-shrink-0">1.</span>Imprime la etiqueta en papel normal o adhesivo.</li>
                      <li className="flex items-start gap-2"><span className="font-bold flex-shrink-0">2.</span>Pégala en el paquete bien sellado.</li>
                      <li className="flex items-start gap-2"><span className="font-bold flex-shrink-0">3.</span><strong>Deposita en el mostrador de cualquier correo USPS de PR — sin hacer fila.</strong></li>
                      <li className="flex items-start gap-2"><span className="font-bold flex-shrink-0">4.</span>El comprador recibirá el tracking por email automáticamente.</li>
                    </ol>
                  </div>
                </div>

                <div className="p-6 pt-0 flex gap-3">
                  <button
                    onClick={() => setEtiquetaModal(null)}
                    className="flex-1 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleGenerarEtiquetaUsps}
                    disabled={generando}
                    className="flex-1 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {generando ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Generando...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-printer-line"></i>
                        Generar y Descargar Etiqueta
                      </span>
                    )}
                  </button>
                </div>
              </>

            ) : resultadoEtiqueta.error ? (
              /* ── Error ── */
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-error-warning-line text-3xl text-red-500"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Error al generar etiqueta</h3>
                <p className="text-gray-500 text-sm mb-6">{resultadoEtiqueta.error}</p>
                <button
                  onClick={() => setResultadoEtiqueta(null)}
                  className="w-full py-3 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Intentar de nuevo
                </button>
              </div>

            ) : (
              /* ── Éxito ── */
              <div className="p-8">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-checkbox-circle-fill text-4xl text-green-500"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">¡Etiqueta USPS Lista!</h3>
                  <p className="text-gray-500 text-sm">{resultadoEtiqueta.servicio}</p>
                  {resultadoEtiqueta.modo === 'test' && (
                    <span className="inline-block mt-2 px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                      Modo Test — Etiqueta de prueba
                    </span>
                  )}
                </div>

                {/* Tracking number */}
                <div className="bg-gray-900 text-white rounded-xl px-6 py-4 text-center mb-4">
                  <p className="text-xs text-gray-400 mb-1 uppercase tracking-wide">Número de Tracking USPS</p>
                  <p className="font-mono text-base font-bold tracking-wider break-all">
                    {resultadoEtiqueta.tracking_number}
                  </p>
                </div>

                {/* Costo descontado */}
                <div className="bg-gray-50 rounded-xl p-4 mb-4 flex justify-between text-sm">
                  <span className="text-gray-500">Costo de etiqueta descontado</span>
                  <span className="font-bold text-gray-900">-${resultadoEtiqueta.costo_etiqueta?.toFixed(2)}</span>
                </div>

                {/* Botones de acción */}
                <div className="space-y-3 mb-4">
                  {resultadoEtiqueta.label_url ? (
                    <a
                      href={resultadoEtiqueta.label_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-3.5 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-download-line text-lg"></i>
                      Descargar Etiqueta PDF
                    </a>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 text-center">
                      <i className="ri-information-line mr-1"></i>
                      La etiqueta PDF se generará cuando el token de Shippo esté en modo live. El tracking ya fue registrado.
                    </div>
                  )}
                  <a
                    href={resultadoEtiqueta.usps_tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-external-link-line"></i>
                    Ver en USPS.com
                  </a>
                </div>

                {/* Estado actualizado */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 space-y-1.5 text-xs text-green-800">
                  <p className="font-bold text-green-900 flex items-center gap-1">
                    <i className="ri-check-double-line"></i>
                    Orden marcada como &quot;Enviada&quot; automáticamente
                  </p>
                  <p className="flex items-start gap-2">
                    <i className="ri-mail-check-line flex-shrink-0 mt-0.5"></i>
                    El comprador recibió el tracking por email.
                  </p>
                  <p className="flex items-start gap-2">
                    <i className="ri-building-line flex-shrink-0 mt-0.5"></i>
                    <strong>Deposita el paquete en cualquier correo USPS de PR sin hacer fila.</strong>
                  </p>
                </div>

                <button
                  onClick={() => { setEtiquetaModal(null); setResultadoEtiqueta(null); }}
                  className="w-full py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL: Tracking / Liberar Fondos
      ══════════════════════════════════════════════════════════════════════ */}
      {trackingModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            {!trackingSuccess ? (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-double-line text-3xl text-green-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Marcar como Entregado</h3>
                  <p className="text-gray-500 text-sm">
                    Ingresa el número de tracking para confirmar la entrega y liberar tus fondos.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Orden</span>
                    <span className="font-semibold text-gray-900">#{trackingModal.id.slice(0, 8).toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Monto a liberar</span>
                    <span className="font-bold text-green-700">${trackingModal.monto_vendedor?.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Número de Tracking</label>
                  <input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Ej: 9400111899223XXXXXXX"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-gray-900 text-sm font-mono"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => { setTrackingModal(null); setTrackingNumber(''); }}
                    className="flex-1 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleLiberarFondos}
                    disabled={procesandoTracking || !trackingNumber.trim()}
                    className="flex-1 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {procesandoTracking ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Procesando...
                      </span>
                    ) : (
                      <><i className="ri-check-line mr-2"></i>Confirmar Entrega</>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-checkbox-circle-fill text-3xl text-green-500"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">¡Fondos Liberados!</h3>
                  <p className="text-gray-500 text-sm mb-4">
                    Tu pago de ${trackingModal.monto_vendedor?.toFixed(2)} ha sido procesado.
                  </p>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <p className="text-sm text-green-800">
                      <i className="ri-check-line mr-1"></i>
                      El dinero será depositado en tu cuenta bancaria en 1-2 días hábiles.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setTrackingModal(null); setTrackingSuccess(false); setTrackingNumber(''); }}
                  className="w-full py-3 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Entendido
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MisVentas;
